import { ReactNode, useId } from 'react';

import { Column, Row } from '../../Containers';
import { Skeleton } from '../../UI';

import { Label } from '../Label';
import { Checkbox, CheckboxProps, checkboxSizeConfig } from '../Checkbox';

import { cn } from 'styles';

import { WithLabelCommonProps } from './types';

export type LabeledCheckboxProps = Omit<WithLabelCommonProps, 'orientation'> &
  CheckboxProps & {
    description?: ReactNode;
    descriptionGap?: WithLabelCommonProps['gap'];
    descriptionClassName?: string;

    /**
     * Defaults to `right`
     */
    labelSide?: 'left' | 'right';

    /**
     * If loading:
     *
     * - `check-only`: Shows a tiny loader of the checkbox size
     * - `row`: Shows a loading bar for the entire width of the container
     *
     * Defaults to `check-only`
     */
    loadingStyle?: 'check-only' | 'row';
  };

export function LabeledCheckbox({
  containerClassName,
  fullWidth,
  gap = '2',

  label,
  labelClassName,
  labelSide = 'right',
  labelIcon: LabelIcon,
  description,
  descriptionGap,
  descriptionClassName,

  loading,
  loadingStyle = 'check-only',
  error,

  ...inputProps
}: LabeledCheckboxProps) {
  const defaultId = useId();

  const hasError = !!error;

  const id = inputProps.id ?? defaultId;

  const actualLabel = (
    <Label className={labelClassName} htmlFor={id}>
      {LabelIcon && (typeof LabelIcon === 'function' ? <LabelIcon size={14} /> : LabelIcon)}
      {label}
    </Label>
  );

  const hasErrorText = hasError && typeof error == 'string';
  const showDescription = description || hasErrorText;

  const labelAndDescription = showDescription ? (
    <Column gap={descriptionGap ?? '1.5'}>
      {actualLabel}

      <span
        className={cn(
          'text-xs',
          hasErrorText ? 'text-destructive' : 'text-muted-foreground',
          descriptionClassName,
        )}
      >
        {hasErrorText ? error : description}
      </span>
    </Column>
  ) : (
    actualLabel
  );

  if (loadingStyle === 'row' && loading) return <Skeleton height={36} />;

  return (
    <Row fullWidth={fullWidth} className={containerClassName} gap={gap}>
      {label && labelSide === 'left' && labelAndDescription}

      {loading ? (
        <Skeleton className={checkboxSizeConfig[inputProps.size ?? 'medium'].sizeClass} />
      ) : (
        <Checkbox
          className={cn(inputProps.className, hasError && 'border-destructive')}
          {...inputProps}
          id={id}
        />
      )}

      {label && labelSide === 'right' && labelAndDescription}
    </Row>
  );
}
